import { formatPhoneNumber } from '~/components/filters';
import type { TenDlcExportField } from '~/components/kiosk/kiosk-ten-dlc/composables/useTenDlcExport';
import {
    brandVerificationLabel,
    campaignStatusLabels,
    dcaCompletion,
    entityTypeLabels,
    sourceLabels,
    termsLabel,
    tmobileBrandTierLabels,
    useCaseLabels,
    vendorLabels,
    verifiedLabel,
} from '~/components/kiosk/kiosk-ten-dlc/tenDlcLabels';
import type { TcrBrand, TcrCampaign, TcrPhoneNumber } from '~/services/adminTenDlcClient.types';
import { csvDateTime } from '~/utils/files';

export const brandExportFields: TenDlcExportField<TcrBrand>[] = [
    { header: 'Brand ID', value: (brand) => brand.brand_id },
    { header: 'Source', value: (brand) => sourceLabels[brand.source] },
    { header: 'Name', value: (brand) => brand.display_name ?? '' },
    { header: 'EIN', value: (brand) => brand.universal_ein ?? '' },
    { header: 'Entity Type', value: (brand) => (brand.entity_type ? entityTypeLabels[brand.entity_type] : '') },
    { header: 'Verification', value: (brand) => brandVerificationLabel(brand.verified, brand.identity_status) },
    { header: 'Trust Score', value: (brand) => brand.vetting_score ?? '' },
    { header: 'Campaigns', value: (brand) => brand.campaigns_count },
    { header: 'Numbers', value: (brand) => brand.phone_numbers_count },
    { header: 'Registered', value: (brand) => csvDateTime(brand.registered_at) },
];

export const campaignExportFields: TenDlcExportField<TcrCampaign>[] = [
    { header: 'Campaign ID', value: (campaign) => campaign.campaign_id },
    { header: 'Source', value: (campaign) => sourceLabels[campaign.source] },
    { header: 'Brand ID', value: (campaign) => campaign.brand_id ?? '' },
    { header: 'Brand', value: (campaign) => campaign.brand_display_name ?? '' },
    {
        header: 'Brand Verification',
        value: (campaign) => (campaign.brand_id ? verifiedLabel(campaign.brand_verified) : ''),
    },
    { header: 'Brand Trust Score', value: (campaign) => campaign.brand_vetting_score ?? '' },
    { header: 'Use Case', value: (campaign) => (campaign.usecase ? useCaseLabels[campaign.usecase] : '') },
    { header: 'Status', value: (campaign) => (campaign.status ? campaignStatusLabels[campaign.status] : '') },
    { header: 'DCA Complete', value: (campaign) => dcaCompletion(campaign)?.label ?? 'N/A' },
    { header: 'AT&T Class', value: (campaign) => campaign.att_message_class ?? '' },
    {
        header: 'T-Mobile Tier',
        value: (campaign) => (campaign.tmobile_brand_tier ? tmobileBrandTierLabels[campaign.tmobile_brand_tier] : ''),
    },
    { header: 'Available AT&T Class', value: (campaign) => campaign.available_att_message_class ?? '' },
    {
        header: 'Available T-Mobile Tier',
        value: (campaign) =>
            campaign.available_tmobile_brand_tier ? tmobileBrandTierLabels[campaign.available_tmobile_brand_tier] : '',
    },
    {
        header: 'Terms',
        value: (campaign) =>
            'twilio' === campaign.source
                ? 'N/A'
                : null === campaign.better_terms_available
                ? ''
                : termsLabel(campaign.better_terms_available),
    },
    { header: 'Numbers', value: (campaign) => campaign.phone_numbers_count },
];

export const phoneNumberExportFields: TenDlcExportField<TcrPhoneNumber>[] = [
    { header: 'Phone Number', value: (phoneNumber) => formatPhoneNumber(phoneNumber.phone_number) },
    { header: 'Vendor', value: (phoneNumber) => vendorLabels[phoneNumber.vendor] },
    { header: 'Brand ID', value: (phoneNumber) => phoneNumber.brand_id ?? '' },
    { header: 'Brand', value: (phoneNumber) => phoneNumber.brand_display_name ?? '' },
    {
        header: 'Brand Verification',
        value: (phoneNumber) => (phoneNumber.brand_id ? verifiedLabel(phoneNumber.brand_verified) : ''),
    },
    { header: 'Brand Trust Score', value: (phoneNumber) => phoneNumber.brand_vetting_score ?? '' },
    { header: 'Campaign ID', value: (phoneNumber) => phoneNumber.campaign_id ?? '' },
    { header: 'Team ID', value: (phoneNumber) => phoneNumber.team_id ?? '' },
    { header: 'Team', value: (phoneNumber) => phoneNumber.team_name ?? '' },
];
